import { push } from 'redux-first-history';
import { all, takeEvery, put, call, select } from 'redux-saga/effects';
import actions from './actions';
import unitGridActions from './unitsGrid/actions';
import Api from '../../../../helpers/api';
import { fieldValidations, resolvedErrorMessage, sendRequest } from '../../../../helpers/saga';
import { logger } from '../../../../helpers/logger';
import notifications from '../../../../redux/notifications/actions';
import { keyByProperty } from '../../../../helpers/core';
import { scrollToFirstError } from '../../../../helpers/scrollToFirstError';
import { isFinanceOrAdminOrSystemAdminRole } from '../../../../helpers/userRoles';
import { t } from '../../../../i18n';
import { translationsRootKey } from '../../../../settings';

const api = new Api('divisions/:id');
const divisionsListApi = new Api('divisions');
const administratorsListApi = new Api('administrators/list');
const unitsApi = new Api('units/list');

function* loadData({ payload }) {
  try {
    const { id, isEdit, role } = payload;
    const result = yield call(sendRequest, divisionsListApi.load, { id }, notifications.REQUEST_ERROR, null, true);

    if (result) {
      const { data } = result;
      yield put(
        actions.loadDataSuccess({
          data,
        }),
      );

      if (isEdit) {
        yield put(
          actions.edit({
            id,
            role,
            division: {
              ...data,
              address: '',
            },
          }),
        );
      }
    }
  } catch (error) {
    logger.error(error);
  }
}

function* updateDivision(data) {
  try {
    const { payload } = data;
    const { editForm, id } = payload;

    const propsArray = Object.values(editForm);
    const division = keyByProperty(propsArray, 'name', prop => prop.value);

    if (!division.breaks_enabled) {
      division.breaks_duration = 0;
      division.breaks_compensation = false;
    }

    const { account_manager, activation_rep, sales_rep, state, ...otherProps } = division;

    const child_ids = [];
    division.units.forEach(unit => child_ids.push(unit.value));

    if (!child_ids.length) {
      yield put(
        actions.saveError({
          data: {
            base: [t('dash.divisions.division.unit_missed_error')],
          },
        }),
      );
    }

    const result = child_ids.length
      ? yield call(
          sendRequest,
          api.updateItem,
          {
            data: {
              division: {
                ...otherProps,
                account_manager_id: account_manager,
                activation_rep_id: activation_rep,
                sales_rep_id: sales_rep,
                child_ids,
                country_id: division.country,
                state_id: state,
              },
            },
            id,
          },
          notifications.REQUEST_ERROR,
        )
      : null;

    if (result) {
      if (result.data) {
        yield put(actions.saveSuccess());

        yield put(push(`/divisions/${id}`));

        yield put(
          notifications.addNotification({
            headline: t('dash.common.notification.success_headline'),
            type: 'success',
            message: t('dash.common.notification.updated_successfully', {
              item: t(`${translationsRootKey}.divisions.division.division_title`),
            }),
          }),
        );
      } else {
        const validations = fieldValidations(result) ?? {};
        const base = !child_ids.length ? [t('dash.divisions.division.unit_missed_error')] : null;

        // A refused write (403 and the like) resolves without field validations; say so instead of marking fields.
        if (base || Object.keys(validations).length) {
          yield put(actions.saveError({ data: { ...validations, base } }));
        } else {
          yield put(notifications.requestError({ error: resolvedErrorMessage(result) }));
        }
      }
    }
  } catch (error) {
    logger.error(error);
  }
}

function* addDivision(data) {
  try {
    const { payload } = data;
    const { editForm } = payload;

    const propsArray = Object.values(editForm);
    const division = keyByProperty(propsArray, 'name', prop => prop.value);

    if (!division.breaks_enabled) {
      division.breaks_duration = 0;
      division.breaks_compensation = false;
    }

    const { account_manager, activation_rep, sales_rep, state, ...otherProps } = division;

    const child_ids = [];
    division.units.forEach(unit => child_ids.push(unit.value));

    if (!child_ids.length) {
      yield put(
        actions.saveError({
          data: {
            base: [t('dash.divisions.division.unit_missed_error')],
          },
        }),
      );
    }

    const result = child_ids.length
      ? yield call(
          sendRequest,
          divisionsListApi.createItem,
          {
            data: {
              division: {
                ...otherProps,
                account_manager_id: account_manager,
                activation_rep_id: activation_rep,
                sales_rep_id: sales_rep,
                child_ids,
                country_id: division.country,
                state_id: state,
              },
            },
          },
          notifications.REQUEST_ERROR,
        )
      : null;

    if (result) {
      if (result.data) {
        const { id } = result.data;
        yield put(actions.saveSuccess());

        yield put(push(`/divisions/${id}`));

        yield put(
          notifications.addNotification({
            headline: t('dash.common.notification.success_headline'),
            type: 'success',
            message: t('dash.common.notification.created_successfully', {
              item: t(`${translationsRootKey}.divisions.division.division_title`),
            }),
          }),
        );
        yield put(unitGridActions.loadData({ ...payload, id }));
      } else {
        const validations = fieldValidations(result);

        // A refused write (403 and the like) resolves without field validations; say so instead of marking fields.
        if (validations) {
          yield put(actions.saveError({ data: validations }));
        } else {
          yield put(notifications.requestError({ error: resolvedErrorMessage(result) }));
        }
      }
    }
  } catch (error) {
    logger.error(error);
  }
}

function* loadFormData({ payload }) {
  try {
    const result = yield call(sendRequest, divisionsListApi.loadFormData, {}, notifications.REQUEST_ERROR);

    if (result) {
      yield put(actions.loadFormDataSuccess(result));

      if (payload.id === 'add') {
        yield put(actions.create({ role: payload.role }));
      }
    }
  } catch (error) {
    logger.error(error);
  }
}

function* loadManagersFormData({ payload }) {
  try {
    const isAcceptedRole = isFinanceOrAdminOrSystemAdminRole(payload.role);
    const result = isAcceptedRole
      ? yield call(sendRequest, administratorsListApi.load, { search: { pageSize: 2000 } }, notifications.REQUEST_ERROR)
      : null;

    if (result) {
      const { data } = result.data;

      yield put(
        actions.loadManagersFormData({
          data,
        }),
      );
    }
  } catch (error) {
    logger.error(error);
  }
}

function* filterByLegalEntity(data) {
  const isLegalEntityEnabled = yield select(state =>
    Boolean(state.StartAppSettings?.feature_flags?.legal_entity_enabled),
  );

  if (!isLegalEntityEnabled) return data;

  const legalEntityId = yield select(
    state => state.Division.division?.legal_entity?.id || state.Division.editForm?.legal_entity_id?.value,
  );

  return legalEntityId ? data.filter(item => item.legal_entity_id === legalEntityId) : data;
}

function* filterUnits({ payload }) {
  try {
    const { filter } = payload;
    let result = null;

    if (filter) {
      result = yield call(
        sendRequest,
        unitsApi.load,
        { search: { filter, pageSize: 100, column: 'name' } },
        notifications.REQUEST_ERROR,
      );
    }

    if (result) {
      const { data } = result.data;
      const filtered = yield call(filterByLegalEntity, data);

      yield put(actions.filterUnitsSuccess({ data: filtered }));
    } else if (!filter) {
      yield put(actions.filterUnitsSuccess({ data: [] }));
    }
  } catch (error) {
    logger.error(error);
  }
}

export default function* rootSaga() {
  yield all([
    takeEvery(actions.LOAD, loadData),
    takeEvery(actions.SAVE, updateDivision),
    takeEvery(actions.ADD, addDivision),
    takeEvery(actions.EDIT, loadFormData),
    takeEvery(actions.LOAD_FORM_DATA, loadFormData),
    takeEvery(actions.EDIT, loadManagersFormData),
    takeEvery(actions.CREATE, loadManagersFormData),
    takeEvery(actions.FILTER_UNITS, filterUnits),
    takeEvery(actions.SAVE_ERROR, scrollToFirstError),
  ]);
}
